import { IInteractionsGetQueryParameters } from "@edgetier/watchtower-types";
import { IUseQueryParametersOptions, useQueryParameters } from "@edgetier/client-components";
import { parseISO } from "date-fns";
import { parseUrlArray } from "@edgetier/utilities";
import { parseUrlBoolean } from "utilities/parse-url-boolean";

type ITransforms = Record<keyof IInteractionsGetQueryParameters, (value: string) => any>;

/**
 * Parse parameters from the URL to filter interactions.
 * @param defaults Any defaults for missing parameters from the URL.
 * @returns        Search parameters.
 */
export const useInteractionQueryParameters = (
    options: IUseQueryParametersOptions<IInteractionsGetQueryParameters> = {}
): IInteractionsGetQueryParameters => {
    const transforms: ITransforms = {
        agentId: parseUrlArray,
        anomalyId: String,
        agentStateType: parseUrlArray,
        endDateTime: parseISO,
        interactionType: parseUrlArray,
        languageIsoCode: String,
        limit: Number,
        groupBy: parseUrlArray,
        interactionState: parseUrlArray,
        isQueryResolved: parseUrlBoolean,
        hasFrustration: parseUrlBoolean,
        hasGratitude: parseUrlBoolean,
        hasPraise: parseUrlBoolean,
        highlightMaximumWords: Number,
        highlightMinimumWords: Number,
        issue: String,
        isAgentSearch: parseUrlBoolean,
        isBotSearch: parseUrlBoolean,
        isCustomerSearch: parseUrlBoolean,
        isMetadataSearch: parseUrlBoolean,
        isSystemSearch: parseUrlBoolean,
        maximumAgentFirstResponseTime: Number,
        maximumAgentMessagesCount: Number,
        maximumAgentSatisfaction: Number,
        maximumAgentScore: Number,
        maximumBotMessagesCount: Number,
        maximumCustomerMessagesCount: Number,
        maximumCustomerSatisfaction: Number,
        maximumExperienceScore: Number,
        maximumFirstResponseTimeSeconds: Number,
        maximumHandlingTimeSeconds: Number,
        maximumMeanResponseSeconds: Number,
        maximumMessagesCount: Number,
        maximumNetPromoterScore: Number,
        maximumQueueTimeSeconds: Number,
        maximumServiceLevelAgreementPercentage: Number,
        minimumAgentFirstResponseTime: Number,
        minimumAgentMessagesCount: Number,
        minimumAgentSatisfaction: Number,
        minimumAgentScore: Number,
        minimumBotMessagesCount: Number,
        minimumCustomerMessagesCount: Number,
        minimumCustomerSatisfaction: Number,
        minimumExperienceScore: Number,
        minimumFirstResponseTimeSeconds: Number,
        minimumHandlingTimeSeconds: Number,
        minimumMeanResponseSeconds: Number,
        minimumMessagesCount: Number,
        minimumNetPromoterScore: Number,
        minimumQueueTimeSeconds: Number,
        minimumServiceLevelAgreementPercentage: Number,
        orderBy: String,
        startDateTime: parseISO,
        search: String,
        isMessageSearch: parseUrlBoolean,
        tagGroupId: parseUrlArray,
        agentTagGroupId: parseUrlArray,
        tagId: parseUrlArray,
        agentTagId: parseUrlArray,
        textSearchType: String,
        timeZone: String,
        webSearchSyntax: parseUrlBoolean,
        writingIssueType: parseUrlArray,
    };

    return useQueryParameters(transforms, {
        ...options,
        defaults: { ...(options.defaults ?? {}), isMessageSearch: true, webSearchSyntax: true },
    });
};
